import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Typography, makeStyles, Grid } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { FormattedMessage, useIntl } from 'react-intl';
import { useContentStyles } from './styles';
import { formatBillingStatusDisplayText } from './util';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    marginBottom: theme.spacing(2)
  },
  alert: {
    alignItems: 'center',
    width: '100%'
  },
  detailsBox: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[300]}`
  },
  detailRow: {
    marginTop: theme.spacing(1),
    display: 'flex',
    '&:last-child': {
      marginBottom: 0
    }
  },
  detailLabel: {
    flexShrink: 0,
    minWidth: '48px',
    marginRight: theme.spacing(1)
  },
  fromLabel: {
    flex: 1
  },
  toLabel: {
    flex: 1
  },
  billingStatusLabel: {
    flex: 1
  },
  notesContainer: {
    marginTop: theme.spacing(3)
  },
  notesItem: {
    marginLeft: theme.spacing(0.75)
  }
}));

const ResultMoveTimeStep = ({
  timeEntrySummary,
  moveTimeData,
  batchResults
}) => {
  const classes = useStyles();
  const contentClasses = useContentStyles();
  const { formatMessage } = useIntl();

  const modifiedCount = batchResults?.successCount;
  const totalCount = timeEntrySummary?.count ?? 0;
  const totalHours = timeEntrySummary?.totalHours ?? 0;

  const isPartialMove = totalCount !== modifiedCount;

  const successMessageValues = useMemo(
    () => ({
      count: modifiedCount,
      hours: totalHours,
      bold: chunks => <strong>{chunks}</strong>
    }),
    [modifiedCount, totalHours]
  );

  const successMessageValuesWithoutHours = useMemo(
    () => ({
      count: modifiedCount,
      bold: chunks => <strong>{chunks}</strong>
    }),
    [modifiedCount]
  );

  const entriesNotMovedValues = useMemo(
    () => ({
      count: totalCount - modifiedCount
    }),
    [modifiedCount, totalCount]
  );

  return (
    <div className={contentClasses.stepContent}>
      <Grid container spacing={0}>
        <div className={classes.root}>
          <Alert
            severity="success"
            className={classes.alert}
            data-qe-id="SuccessMessage"
            icon={false}
          >
            <Typography variant="body1">
              <FormattedMessage
                id={
                  isPartialMove
                    ? 'moveTimeDialog.successfullyMovedPartial'
                    : 'moveTimeDialog.successfullyMoved'
                }
                values={
                  isPartialMove
                    ? successMessageValuesWithoutHours
                    : successMessageValues
                }
              />
            </Typography>
            <div className={classes.detailRow}>
              <Typography variant="body1" className={classes.detailLabel}>
                <FormattedMessage id="moveTimeDialog.from" />:
              </Typography>
              <Typography variant="body1" className={classes.fromLabel}>
                {moveTimeData.source.project?.displayText} /{' '}
                {moveTimeData.source.task?.fullPathText ||
                  moveTimeData.source.task?.displayText}
              </Typography>
            </div>
            <div className={classes.detailRow}>
              <Typography variant="body1" className={classes.detailLabel}>
                <FormattedMessage id="moveTimeDialog.to" />:
              </Typography>
              <Typography variant="body1" className={classes.toLabel}>
                {moveTimeData.destination.project?.displayText} /{' '}
                {moveTimeData.destination.task?.fullPathText ||
                  moveTimeData.destination.task?.displayText}
              </Typography>
            </div>
            <div className={classes.detailRow}>
              <Typography variant="body1" className={classes.detailLabel} />
              <Typography
                variant="body1"
                className={classes.billingStatusLabel}
              >
                {formatBillingStatusDisplayText(
                  moveTimeData.billingStatus,
                  formatMessage
                )}
              </Typography>
            </div>
          </Alert>
        </div>
        {totalCount !== modifiedCount && (
          <div className={classes.root}>
            <Alert
              severity="warning"
              className={classes.alert}
              data-qe-id="WarningMessage"
              icon={false}
            >
              <Typography variant="body1">
                <FormattedMessage
                  id="moveTimeDialog.entriesNotMoved"
                  values={entriesNotMovedValues}
                />
              </Typography>
            </Alert>
          </div>
        )}
      </Grid>
      <div className={classes.notesContainer}>
        <Typography variant="caption">
          <FormattedMessage id="moveTimeDialog.notesTitle" />
        </Typography>
        <div className={classes.notesItem}>
          <Typography variant="caption">
            <FormattedMessage id="moveTimeDialog.notesRecalculation" />
          </Typography>
        </div>
        <div className={classes.notesItem}>
          <Typography variant="caption">
            <FormattedMessage id="moveTimeDialog.notesTimesheetAudit" />
          </Typography>
        </div>
      </div>
    </div>
  );
};

ResultMoveTimeStep.propTypes = {
  timeEntrySummary: PropTypes.object.isRequired,
  moveTimeData: PropTypes.object.isRequired,
  batchResults: PropTypes.object
};

export default ResultMoveTimeStep;
